/*
 * These classes exist to help replace reactstrap. As reactstrap components
 * are removed, swap them for a plain element plus the equivalent class
 * here so we can drop the dependency without changing layout.
 *
 * `form-group` mirrors the default bottom margin of Bootstrap 4's
 * `.form-group` (1rem). With `--spacing-1` at 6px, `* 8 / 3` resolves to
 * exactly 16px.
 */
.form-group {
  /* Was `margin-bottom: 1rem` on reactstrap's FormGroup. */
  margin-bottom: calc(var(--spacing-1) * 8 / 3);
}

/*
 * The grid classes below replace reactstrap's `Row` and `Col`, which render
 * Bootstrap 4's flex grid. The 15px gutter is Bootstrap 4's default
 * (`$grid-gutter-width: 30px`, halved per side); it does not land on the 6px
 * `--spacing-*` scale, so the literal is kept to preserve the exact layout.
 * Breakpoints are Bootstrap 4's defaults (sm 576, md 768, lg 992, xl 1200).
 * Only the breakpoint+span combinations actually used by the migrated files
 * are defined.
 */

/* Was `<Row>` (Bootstrap `.row`). */
.row {
  display: flex;
  flex-wrap: wrap;
  margin-left: -15px;
  margin-right: -15px;
}

/*
 * Shared column padding/position. Bootstrap applies this base to every column
 * class, so listing the classes once here keeps the gutter rule in a single
 * place instead of repeating it on each modifier.
 */
.col,
.col-12,
.col-sm-5,
.col-sm-6,
.col-sm-7,
.col-sm-12,
.col-md-4,
.col-md-6,
.col-md-7,
.col-md-8,
.col-md-12,
.col-lg-3,
.col-lg-4,
.col-lg-6,
.col-lg-7,
.col-lg-8,
.col-lg-12,
.col-xl-3,
.col-xl-4,
.col-xl-6,
.col-xl-8,
.col-xl-9,
.col-xl-12 {
  padding-left: 15px;
  padding-right: 15px;
  position: relative;
  width: 100%;
}

/* Was `<Col>` with no width props (Bootstrap `.col`, equal-width). */
.col {
  flex-basis: 0;
  flex-grow: 1;
  max-width: 100%;
}

/* Was `<Col xs="12">` (Bootstrap `.col-12`; the xs tier has no infix). */
.col-12 {
  flex: 0 0 100%;
  max-width: 100%;
}

/* Was `<Col sm="…">` (Bootstrap `.col-sm-*`, applies at ≥576px). */
@media (min-width: 576px) {
  .col-sm-5 {
    flex: 0 0 41.666667%;
    max-width: 41.666667%;
  }
  .col-sm-6 {
    flex: 0 0 50%;
    max-width: 50%;
  }
  .col-sm-7 {
    flex: 0 0 58.333333%;
    max-width: 58.333333%;
  }
  .col-sm-12 {
    flex: 0 0 100%;
    max-width: 100%;
  }
}

/* Was `<Col md="…">` (Bootstrap `.col-md-*`, applies at ≥768px). */
@media (min-width: 768px) {
  .col-md-4 {
    flex: 0 0 33.333333%;
    max-width: 33.333333%;
  }
  .col-md-6 {
    flex: 0 0 50%;
    max-width: 50%;
  }
  .col-md-7 {
    flex: 0 0 58.333333%;
    max-width: 58.333333%;
  }
  .col-md-8 {
    flex: 0 0 66.666667%;
    max-width: 66.666667%;
  }
  .col-md-12 {
    flex: 0 0 100%;
    max-width: 100%;
  }
}

/* Was `<Col lg="…">` (Bootstrap `.col-lg-*`, applies at ≥992px). */
@media (min-width: 992px) {
  .col-lg-3 {
    flex: 0 0 25%;
    max-width: 25%;
  }
  .col-lg-4 {
    flex: 0 0 33.333333%;
    max-width: 33.333333%;
  }
  .col-lg-6 {
    flex: 0 0 50%;
    max-width: 50%;
  }
  .col-lg-7 {
    flex: 0 0 58.333333%;
    max-width: 58.333333%;
  }
  .col-lg-8 {
    flex: 0 0 66.666667%;
    max-width: 66.666667%;
  }
  .col-lg-12 {
    flex: 0 0 100%;
    max-width: 100%;
  }
}

/* Was `<Col xl="…">` (Bootstrap `.col-xl-*`, applies at ≥1200px). */
@media (min-width: 1200px) {
  .col-xl-3 {
    flex: 0 0 25%;
    max-width: 25%;
  }
  .col-xl-4 {
    flex: 0 0 33.333333%;
    max-width: 33.333333%;
  }
  .col-xl-6 {
    flex: 0 0 50%;
    max-width: 50%;
  }
  .col-xl-8 {
    flex: 0 0 66.666667%;
    max-width: 66.666667%;
  }
  .col-xl-9 {
    flex: 0 0 75%;
    max-width: 75%;
  }
  .col-xl-12 {
    flex: 0 0 100%;
    max-width: 100%;
  }
}
